'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { toast } from '~/utils/toast';
import { api, type RouterInputs } from '~/utils/api';
import { serverError } from '~/utils/serverError';

import { ThumbsUp as ThumbsUpIcon } from 'lucide-react';
import { PolicyFeedbackAnnotationScope, Permission } from '@prisma/client';

import Button from '../Button';
import Quote from '../Quote';
import Tooltip from '../Tooltip';
import AppSpinner from '~/components/Loading/AppSpinner';
import { type PolicyCardProps } from './PolicyCard';
import { usePopupManager } from '~/hooks/usePopupManager';
import { FeedbackTeachPanel, sharedFeedbackText } from './FeedbackItemFrame';
import { useWorkspace } from '~/contexts/WorkspaceContext';
import { policyEnginePath } from '~/utils/policyEnginePath';
import useAbility from '~/hooks/useAbility';

const noSettledIds: string[] = [];

type IgnoreFeedbackInput = RouterInputs['feedback']['ignoreFeedback'];

type TruePositiveMark = {
  PolicyFeedbackAnnotation?: Array<{ scope: PolicyFeedbackAnnotationScope }> | null;
};

export function hasTruePositiveMark(items: TruePositiveMark[]) {
  return items.some((item) =>
    item.PolicyFeedbackAnnotation?.some(
      (annotation) => annotation.scope === PolicyFeedbackAnnotationScope.TRUE_POSITIVE,
    ),
  );
}

export type ItemTeachingResult = 'up' | 'ignore';

export function itemTeachingResult(item: TruePositiveMark): ItemTeachingResult | null {
  const scope = item.PolicyFeedbackAnnotation?.[0]?.scope;
  if (!scope) return null;
  return scope === PolicyFeedbackAnnotationScope.TRUE_POSITIVE ? 'up' : 'ignore';
}

type ThumbsUpProps = Pick<PolicyCardProps, 'concatenateFeedbacks'> & {
  feedbackId: string;
  type: 'claim' | 'policyFeedback';
  alreadyMarked?: boolean;
  settledIds?: string[];
  onMarked?: (feedbackId: string) => void;
};

export default function ThumbsUp(props: ThumbsUpProps) {
  const {
    concatenateFeedbacks,
    type,
    feedbackId,
    alreadyMarked = false,
    settledIds = noSettledIds,
    onMarked,
  } = props;
  const openFeedbacks = concatenateFeedbacks.filter((item) => !settledIds.includes(item.id));
  const { workspaceId } = useWorkspace();
  const ability = useAbility();
  const canViewTeachQueue = !!ability?.can('do', Permission.VIEW_POLICY_ENGINE);

  const popupId = `thumbs-up-${feedbackId}`;
  const { isOpen, openPopup, closePopup } = usePopupManager(popupId);

  const utils = api.useUtils();
  const { mutateAsync: ignoreFeedback, isLoading } = api.feedback.ignoreFeedback.useMutation();
  const [marked, setMarked] = useState(alreadyMarked);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [markedFeedback, setMarkedFeedback] = useState<
    Pick<IgnoreFeedbackInput, 'markedClaimId' | 'markedPolicyFeedbackId'>
  >({});

  useEffect(() => {
    if (alreadyMarked) setMarked(true);
  }, [alreadyMarked]);

  const markCorrect = async (id: string) => {
    if (marked || savingId || isLoading || !id) return;
    const willSettle = openFeedbacks.filter((item) => item.id !== id).length === 0;
    setSavingId(id);

    try {
      await ignoreFeedback({
        markedClaimId: type === 'claim' ? id : null,
        markedPolicyFeedbackId: type === 'policyFeedback' ? id : null,
        scope: PolicyFeedbackAnnotationScope.TRUE_POSITIVE,
        rejected: false,
      });
      onMarked?.(id);
      if (willSettle) setMarked(true);
      setSavingId(null);
      void utils.dataStore.getAllAnnotations.invalidate();
      toast.teachingPending({
        description: 'This correctly flagged example is pending approval.',
        teachYourAiHref: canViewTeachQueue
          ? policyEnginePath(workspaceId, '/policy-annotations')
          : null,
      });
      closePopup();
    } catch (e) {
      setSavingId(null);
      serverError(e as Error);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      const markedFeedbackId = openFeedbacks[0]?.id || concatenateFeedbacks[0]?.id || feedbackId;
      setMarkedFeedback(
        type === 'claim'
          ? { markedClaimId: markedFeedbackId, markedPolicyFeedbackId: null }
          : { markedClaimId: null, markedPolicyFeedbackId: markedFeedbackId },
      );
    }
  }, [isOpen, concatenateFeedbacks, feedbackId, settledIds]);

  const sharedSelectionText = sharedFeedbackText(concatenateFeedbacks);

  return (
    <>
      <Tooltip
        content={marked ? 'Marked as correctly flagged' : 'Mark as correctly flagged'}
        hideIcon
      >
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={marked ? 'Marked as correctly flagged' : 'Mark as correctly flagged'}
          aria-pressed={marked}
          disabled={!!savingId || isLoading || marked}
          className={marked ? 'opacity-40 transition-none' : undefined}
          onClick={(e) => {
            e.stopPropagation();
            if (marked) return;
            if (openFeedbacks.length > 1) {
              openPopup();
            } else {
              void markCorrect(openFeedbacks[0]?.id || feedbackId);
            }
          }}
        >
          {(savingId || isLoading) && openFeedbacks.length <= 1 ? (
            <AppSpinner size="xs" />
          ) : (
            <ThumbsUpIcon className="size-4" strokeWidth={2} />
          )}
        </Button>
      </Tooltip>
      {isOpen && (
        <FeedbackTeachPanel
          title="Confirm Teaching"
          description="Which policy does this confirm?"
          onClose={closePopup}
          footer={
            <Button
              type="button"
              label="Submit"
              size="sm"
              className="w-full"
              disabled={
                isLoading ||
                !!savingId ||
                (!markedFeedback.markedClaimId && !markedFeedback.markedPolicyFeedbackId)
              }
              isLoading={isLoading || !!savingId}
              onClick={() => {
                const selectedId =
                  type === 'claim'
                    ? markedFeedback.markedClaimId
                    : markedFeedback.markedPolicyFeedbackId;
                if (selectedId) void markCorrect(selectedId);
              }}
            />
          }
        >
          <div className="space-y-1">
            {sharedSelectionText ? (
              <Quote
                text={sharedSelectionText}
                className="mb-2 line-clamp-4 py-1.5 text-[13px]"
                title={sharedSelectionText}
              />
            ) : null}
            {openFeedbacks.map((i) => (
              <label key={i.id} className="block cursor-pointer py-1">
                <span className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="select-feedback"
                    checked={
                      type === 'claim'
                        ? i.id === markedFeedback.markedClaimId
                        : i.id === markedFeedback.markedPolicyFeedbackId
                    }
                    className="border-input size-3.5 shrink-0 accent-foreground leading-none"
                    onChange={(e) =>
                      e.target.checked &&
                      setMarkedFeedback((prev) => ({
                        ...prev,
                        [type === 'claim' ? 'markedClaimId' : 'markedPolicyFeedbackId']: i.id,
                      }))
                    }
                  />
                  <Link
                    className="text-foreground min-w-0 text-[13px] leading-4 font-medium tracking-tight underline decoration-foreground/35 underline-offset-[3px] hover:decoration-foreground"
                    href={policyEnginePath(
                      workspaceId,
                      `/group/${i.policy.policyGroupId}/policy/${i.policy.id}`,
                    )}
                    target="_blank"
                  >
                    {i.policy.name}
                  </Link>
                </span>
                {!sharedSelectionText && i.text ? (
                  <Quote
                    text={i.text}
                    className="mt-1.5 ml-[1.375rem] line-clamp-4 py-1.5 text-[13px]"
                    title={i.text}
                  />
                ) : null}
              </label>
            ))}
          </div>
        </FeedbackTeachPanel>
      )}
    </>
  );
}
